// client/src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { reinitializeToken } from '../utils/api';
import { clearLegacyLocalStorageDrafts } from '../api/assessmentDrafts';

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  requirePasswordChange?: boolean;
  [key: string]: any;
}

interface MFAResponse {
  requiresMFA: true;
  userId: string;
  email: string;
  mfaType?: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  requirePasswordChange: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean | MFAResponse>;
  logout: () => void;
  updateRequirePasswordChange: (value: boolean) => void;
  refreshAuth: () => void;
  updateToken: (token: string | null) => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  requirePasswordChange: false,
  isLoading: false,
  login: async () => false,
  logout: () => {},
  updateRequirePasswordChange: () => {},
  refreshAuth: () => {},
  updateToken: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Phase 2C, Stage 6: one-time, best-effort cleanup of legacy plaintext
  // assessment drafts left over from before drafts moved to encrypted
  // server-side storage (design §9, resolving CLIENT-08). Safe to run on
  // every app load - a no-op once nothing matches. Can be removed after a
  // release or two, once it's had a chance to run on active browsers.
  useEffect(() => {
    clearLegacyLocalStorageDrafts();
  }, []);

  const verifyToken = useCallback(async (token: string, retryCount = 0) => {
    const maxRetries = 2;
    
    try {
      // Ensure the token is set in both places for consistency
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      console.log('Verifying token...', { tokenLength: token.length, attempt: retryCount + 1 });
      
      const response = await api.get('/api/auth/verify');
      const userData = response.data;
      console.log('Verify token response:', userData);
      
      if (!userData || !userData.role) {
        throw new Error('Invalid user data received from server');
      }
      
      setUser(userData);
      setIsAuthenticated(true);
      setRequirePasswordChange(!!userData.requirePasswordChange);
      console.log('Token verification successful');
    } catch (error: any) {
      console.error('Error verifying token:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        attempt: retryCount + 1
      });
      
      // Check if this is a network error and we can retry
      const isNetworkError = !error.response || error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED';
      const isServerError = error.response?.status >= 500;
      
      if ((isNetworkError || isServerError) && retryCount < maxRetries) {
        console.log(`Retrying token verification (attempt ${retryCount + 2}/${maxRetries + 1})...`);
        setTimeout(() => {
          verifyToken(token, retryCount + 1);
        }, 1000 * (retryCount + 1)); // Exponential backoff
        return;
      }
      
      // Only clear auth state if it's actually an auth error
      // Don't clear on network errors or temporary server issues
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log('Authentication failed, clearing token');
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        setIsAuthenticated(false);
        setUser(null);
        setRequirePasswordChange(false);
      } else if (retryCount >= maxRetries) {
        // After max retries, assume network/server issues
        // Clear the token and redirect to login for safety
        console.warn('Token verification failed after retries, clearing authentication state');
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
        setIsAuthenticated(false);
        setUser(null);
        setRequirePasswordChange(false);
      }
    } finally {
      if (retryCount === 0 || retryCount >= maxRetries) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Ensure token is properly initialized in API client
      reinitializeToken();
      verifyToken(token);
    } else {
      setIsLoading(false);
    }
  }, [verifyToken]);

  const login = async (email: string, password: string) => {
    try {
      // Clear any previous auth data before attempting login
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      
      const response = await api.post('/api/auth/login', { email, password });
      
      // Check if MFA is required
      if (response.data.requiresMFA) {
        // MFA required - return the MFA challenge data
        // The calling component should handle the MFA flow
        return response.data;
      }
      
      const { token, user: userData } = response.data;
      
      // Validate we have the required data
      if (!token || !userData) {
        throw new Error('Invalid login response from server');
      }
      
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      console.log('Login response user data:', userData);
      setUser(userData);
      setIsAuthenticated(true);
      setRequirePasswordChange(!!userData.requirePasswordChange);
      
      if (userData.requirePasswordChange) {
        navigate('/change-password');
      } else if (userData.role === 'counsellor') {
        // Counsellors who are Active or Pending Agreement go to dashboard
        // (Pending Agreement counsellors see the ContractSigningBanner on dashboard)
        const dashboardStatuses = ['Active', 'Pending Agreement', 'Pending Self-Completion', 'Legal Information Requested'];
        if (dashboardStatuses.includes(userData.applicationStatus)) {
          navigate('/counsellor/dashboard');
        } else {
          navigate('/counsellor/onboarding');
        }
      } else {
        navigate('/');
      }
      return true;
    } catch (error: any) {
      console.error('Login error:', error);
      // Extract error message from response or use default
      const errorMessage = 
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.message || 
        'Invalid email or password. Please try again.';
      
      // Throw a standardized error object that will be caught by the form handler
      throw new Error(errorMessage);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    // Phase 2C, Stage 6: a second line of defence against legacy
    // plaintext assessment drafts, for any browser profile that stays
    // logged in and never re-triggers the bootstrap cleanup above.
    clearLegacyLocalStorageDrafts();
    delete api.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
    setRequirePasswordChange(false);
    navigate('/login');
  };

  const updateRequirePasswordChange = (value: boolean) => {
    setRequirePasswordChange(value);
  };

  const refreshAuth = () => {
    const token = localStorage.getItem('token');
    if (token) {
      console.log('Manually refreshing authentication...');
      reinitializeToken();
      verifyToken(token);
    } else {
      console.log('No token found for refresh');
      setIsAuthenticated(false);
      setUser(null);
      setRequirePasswordChange(false);
    }
  };

  const updateToken = (newToken: string | null) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      verifyToken(newToken);
    } else {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
      setIsAuthenticated(false);
      setUser(null);
      setRequirePasswordChange(false);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>; // Replace with proper loading component
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        requirePasswordChange,
        isLoading,
        login,
        logout,
        updateRequirePasswordChange,
        refreshAuth,
        updateToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}